import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimePostgresInsertPayload } from '@supabase/supabase-js';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { useOrgMembersIndex } from '../../lib/useOrgMembersIndex';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { askCampaignStream, type Citation, type AskToolCall } from '../../services/geminiService';
import {
  askThreadScopeKey,
  listAskThreads,
  loadAskThreadMessages,
  createAskThread,
  appendAskMessage,
  updateAskThreadTitle,
  deleteAskThread,
  mapAskMessageRow,
  type AskThreadSummary,
  type AskMessageRow,
} from '../../services/askThreadService';
import { type ChatMessage } from './CampaignAsk';
import { CampaignAskLauncher } from './CampaignAskLauncher';
import { CampaignAskModal } from './CampaignAskModal';

/**
 * Generate a collision-resistant id, falling back to a timestamp + random suffix
 * when `crypto.randomUUID` is unavailable (older browsers, or any non-secure
 * HTTP context where the API is not exposed). Mirrors the guard used in
 * `CreateOrgStep` and `OrgCoreProvider` so a missing API never throws and freezes
 * the chat before the optimistic turn is added.
 */
function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Order two turns oldest-first by `createdAt`, with the row id as a stable
 * tiebreak. A turn still missing a `createdAt` (an optimistic/streaming turn not
 * yet persisted) is the newest, so it sorts last rather than jumping to the top
 * when a merge runs mid-stream.
 */
function compareTurns(a: ChatMessage, b: ChatMessage): number {
  const aTime = a.createdAt;
  const bTime = b.createdAt;
  if (aTime && bTime) return aTime.localeCompare(bTime) || a.id.localeCompare(b.id);
  if (aTime) return -1;
  if (bTime) return 1;
  return a.id.localeCompare(b.id);
}

/**
 * Reconcile the current turns with incoming rows in one lossless, order-stable
 * pass: an existing id is updated in place (a realtime echo refreshes an
 * optimistic turn's authoritative `createdAt` instead of being dropped by id
 * dedup); streaming/optimistic turns absent from `incoming` are preserved; the
 * result is ordered oldest-first. Used by both the initial load and the realtime
 * handler so neither operation is lossy on its own.
 */
function mergeTurns(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(prev.map((message) => [message.id, message]));
  for (const next of incoming) {
    const existing = byId.get(next.id);
    byId.set(next.id, existing ? { ...existing, createdAt: next.createdAt } : next);
  }
  return [...byId.values()].sort(compareTurns);
}

/** Props for {@link CampaignAskBox}. */
export interface CampaignAskBoxProps {
  /** The interview session (campaign) id to query. */
  interviewId: string;
  /**
   * Restrict the question to a subset of conversations (e.g. a single response).
   * Omit to ask across the whole campaign.
   */
  interviewDataIds?: string[];
  /** Composer placeholder. */
  placeholder?: string;
  /** Called when a citation is clicked (best-effort deep link). */
  onCitationClick?: (citation: Citation) => void;
  /**
   * A question handed over from elsewhere on the page (the Overview's
   * suggestions and card links). Each new `nonce` opens the ask and sends the
   * question in a new conversation.
   */
  request?: { question: string; nonce: number };
  /** Launcher style: a text-box card, or a small button for the Overview's Ask hollie card. */
  variant?: 'card' | 'button';
}

/**
 * Self-contained ask box: a brand-tinted launcher card on the page that opens a
 * focused modal where an owner (or any teammate who can reach the campaign)
 * chats with hollie and gets grounded, cited answers that stream in live.
 *
 * The multi-turn thread is now stored in the database (`ask_threads` /
 * `ask_messages`) rather than localStorage, so it is durable and shared across
 * the team and devices. The three-dots menu starts a new conversation, switches
 * to a previous one, or clears the current one for everyone. Auth and the active
 * org come from context; prior turns are replayed to the server for follow-ups.
 *
 * Remount this component (via a `key` on the scope) when the campaign or
 * conversation changes, so the right thread list loads for the right scope.
 */
export const CampaignAskBox: React.FC<CampaignAskBoxProps> = ({
  interviewId,
  interviewDataIds,
  placeholder,
  onCitationClick,
  request,
  variant = 'card',
}) => {
  const { supabase, session: authSession } = useSupabase();
  const { activeOrg } = useOrg();
  const scopeKey = askThreadScopeKey(interviewDataIds);

  // Resolve a turn's author to a display name for attribution in the shared
  // thread. Org members come from the membership RPC; the current user falls back
  // to their auth profile (covers legacy NULL-org, creator-only threads).
  const membersIndex = useOrgMembersIndex(activeOrg?.id ?? null);
  const currentUserId = authSession?.user?.id ?? null;
  const currentUserName =
    (typeof authSession?.user?.user_metadata?.full_name === 'string'
      ? authSession.user.user_metadata.full_name.trim()
      : '')
    || authSession?.user?.email
    || 'You';
  const resolveAuthorName = useCallback(
    (authorId: string | null | undefined): string => {
      // A null author is a legacy/pre-migration row (created_by defaulted to NULL
      // with no backfill), which can be the current user's own question. Render a
      // neutral, empty attribution rather than mislabeling it as a teammate. Only
      // user turns reach here; an assistant turn always renders as hollie.
      if (!authorId) return '';
      const member = membersIndex[authorId];
      if (member?.name) return member.name;
      if (authorId === currentUserId) return currentUserName;
      return member?.email || '';
    },
    [membersIndex, currentUserId, currentUserName],
  );

  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<AskThreadSummary[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // The freshly-created thread whose AI title is still generating. While set, the
  // modal shows a title skeleton instead of a premature placeholder that would
  // then visibly swap to the real title.
  const [titlePendingId, setTitlePendingId] = useState<string | null>(null);

  // Which thread's turns are currently loaded into `messages`, so switching /
  // opening only re-fetches when the loaded thread actually changed.
  const loadedThreadRef = useRef<string | null>(null);
  // The campaign+scope whose thread list has been loaded, so the mount effect
  // runs once per scope and never re-fires just because the (stable singleton)
  // Supabase client's wrapper identity changed on a re-render.
  const loadedScopeRef = useRef<string | null>(null);

  // Latest-wins token for thread loads. Every load / conversation switch bumps
  // it; a load whose token is stale on resolution is discarded, so an out-of-order
  // response from an earlier thread can never overwrite the active one's turns.
  const loadSeqRef = useRef(0);

  // The active thread id mirrored into a ref so the realtime handler can guard
  // against a late event from a previous thread's channel (removeChannel is
  // async, so an event for thread A can still arrive after switching to B).
  const activeThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  // True once the current conversation could not get a database thread (create
  // failed). It then stays in-memory only for its lifetime, so a later turn never
  // spins up a second thread and fragments the visible conversation in storage.
  const ephemeralRef = useRef(false);

  // Load the conversation list for this campaign+scope on mount / scope change.
  // Guarded so it runs once per scope: a re-render that hands back a new client
  // wrapper identity must not reset state and re-fetch (that would loop).
  useEffect(() => {
    const scopeId = `${interviewId}::${scopeKey}`;
    if (loadedScopeRef.current === scopeId) {
      return undefined;
    }
    loadedScopeRef.current = scopeId;
    let cancelled = false;
    setThreads([]);
    setActiveThreadId(null);
    setMessages([]);
    loadedThreadRef.current = null;
    void (async () => {
      try {
        const list = await listAskThreads(supabase, interviewId, scopeKey);
        if (cancelled) return;
        setThreads(list);
        if (list.length > 0) {
          setActiveThreadId(list[0].id);
        }
      } catch (err) {
        captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.listThreads', {
          sessionId: interviewId,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, interviewId, scopeKey]);

  /** Load a thread's persisted turns into the composer view (latest load wins). */
  const loadThread = useCallback(
    async (threadId: string) => {
      const seq = (loadSeqRef.current += 1);
      try {
        const stored = await loadAskThreadMessages(supabase, threadId);
        // A newer load / switch superseded this one: drop the stale result so it
        // cannot overwrite the active thread's turns or desync loadedThreadRef.
        if (seq !== loadSeqRef.current) return;
        // Merge rather than replace: a reopen mid-stream keeps the in-flight
        // streaming turn (absent from the DB snapshot), and a row that arrived via
        // realtime while this load was in flight is preserved instead of clobbered.
        // On a genuine thread switch the list was already cleared, so this starts
        // from an empty base.
        setMessages((prev) => mergeTurns(prev, stored));
        loadedThreadRef.current = threadId;
      } catch (err) {
        if (seq !== loadSeqRef.current) return;
        captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.loadThread', {
          sessionId: interviewId,
        });
      }
    },
    [supabase, interviewId],
  );

  // Load the active thread's turns whenever the modal is open and the selection
  // changed, regardless of which resolved first (opening before the list loads,
  // or switching). The latest-wins guard in loadThread keeps races correct.
  useEffect(() => {
    if (open && activeThreadId && loadedThreadRef.current !== activeThreadId) {
      void loadThread(activeThreadId);
    }
  }, [open, activeThreadId, loadThread]);

  // Live multi-user sync: when a teammate posts into the open thread, their turn
  // is inserted into ask_messages and broadcast here so it appears without a
  // refresh. Our own inserts echo back too (we persist with the optimistic id),
  // reconciled by id so the echo refreshes the optimistic turn rather than
  // duplicating it. Scope is the open thread only; realtime still enforces the
  // table's SELECT policy, so only the campaign's team receives the rows.
  useEffect(() => {
    if (!open || !activeThreadId) return undefined;
    const channel = supabase
      .channel(`ask_messages:${activeThreadId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ask_messages',
          filter: `thread_id=eq.${activeThreadId}`,
        },
        (payload: RealtimePostgresInsertPayload<AskMessageRow>) => {
          // Guard against a late event from a previous thread's channel: a switch
          // re-subscribes, but removeChannel is async so an in-flight thread-A
          // event can still land here after we moved to thread B.
          if (payload.new?.thread_id !== activeThreadIdRef.current) return;
          const incoming = mapAskMessageRow(payload.new);
          if (!incoming) return;
          // Merge so the echo of our own insert refreshes the optimistic turn's
          // authoritative createdAt (instead of being dropped by id dedup), and
          // teammates' rows interleave in createdAt order rather than arrival order.
          setMessages((prev) => mergeTurns(prev, [incoming]));
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [open, activeThreadId, supabase]);

  const handleOpen = () => setOpen(true);

  // Each new handed-over question opens the ask and starts a new conversation.
  const handledRequestRef = useRef<number | null>(null);
  useEffect(() => {
    if (!request || handledRequestRef.current === request.nonce) return;
    handledRequestRef.current = request.nonce;
    handleNewConversation();
    setOpen(true);
    void handleSubmit({ question: request.question });
  }, [request]);

  const handleClose = () => {
    setOpen(false);
    // The live subscription only runs while open, so re-fetch on the next open to
    // pick up anything inserted while it was torn down.
    loadedThreadRef.current = null;
  };

  const handleNewConversation = () => {
    // Invalidate any in-flight load so it cannot repopulate the cleared thread.
    loadSeqRef.current += 1;
    ephemeralRef.current = false;
    setActiveThreadId(null);
    setMessages([]);
    setInput('');
    setTitlePendingId(null);
    loadedThreadRef.current = null;
  };

  const handleSwitchConversation = (threadId: string) => {
    if (threadId === activeThreadId) return;
    // Clear immediately so the previous thread's turns don't linger; the load
    // effect fetches the new thread (latest-wins guards any in-flight load).
    setMessages([]);
    setActiveThreadId(threadId);
    setTitlePendingId(null);
  };

  const handleDeleteConversation = async () => {
    const threadId = activeThreadId;
    if (!threadId) return;
    try {
      await deleteAskThread(supabase, threadId);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.deleteThread', {
        sessionId: interviewId,
      });
      return;
    }
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    handleNewConversation();
  };

  /**
   * Send the composer's question, or a handed-over one in a fresh conversation
   * (no history, a new thread), so it never lands in an unrelated thread.
   */
  const handleSubmit = async (handover?: { question: string }) => {
    const token = authSession?.access_token;
    const question = handover ? handover.question.trim() : input.trim();
    if (!token || !question || busy) return;

    // Prior turns become the follow-up context for this question.
    const history = handover ? [] : messages.map((m) => ({ role: m.role, content: m.content }));

    // Ensure a thread exists to persist into. If creation fails, the whole
    // conversation stays ephemeral for its lifetime (still answered, just not
    // saved) rather than spinning up a fresh thread on a later turn, which would
    // fragment the visible conversation and silently drop the earlier turns from
    // storage. A new conversation resets the ephemeral flag.
    let threadId = handover ? null : activeThreadId;
    if (!threadId && !ephemeralRef.current) {
      try {
        // Create with no title: the AI title arrives later in the stream and a
        // skeleton stands in until then (no raw-question placeholder to swap out).
        const created = await createAskThread(supabase, {
          interviewId,
          scopeKey,
          interviewDataIds,
          organizationId: activeOrg?.id ?? null,
          title: null,
        });
        threadId = created.id;
        setActiveThreadId(created.id);
        setThreads((prev) => [created, ...prev]);
        setTitlePendingId(created.id);
        loadedThreadRef.current = created.id;
      } catch (err) {
        ephemeralRef.current = true;
        captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.createThread', {
          sessionId: interviewId,
        });
      }
    }

    // Client-generated ids so the realtime echo of our own inserts (same id)
    // reconciles against the optimistic turns already in state. Guarded so a
    // missing crypto.randomUUID (non-secure context) cannot throw before the
    // optimistic turn is added and the input is cleared.
    const userMsgId = randomId();
    const assistantId = randomId();
    const nowIso = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: 'user', content: question, citations: [], authorId: currentUserId, createdAt: nowIso },
      // No createdAt yet: it is stamped at completion (in the finally below), not
      // here. A submit-time value would show the asker a pre-answer time, while
      // leaving it unset until the realtime echo means a dropped/reordered echo
      // could sink the finished answer below a later question. The echo still
      // refreshes it to the authoritative DB persist time when it does arrive.
      { id: assistantId, role: 'assistant', content: '', citations: [], streaming: true },
    ]);
    setInput('');
    setBusy(true);

    // Persist the user turn immediately (best-effort, off the streaming path).
    if (threadId) {
      void appendAskMessage(supabase, threadId, { id: userMsgId, role: 'user', content: question, citations: [] }).catch(
        (err) =>
          captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.persistQuestion', {
            sessionId: interviewId,
          }),
      );
    }

    const patch = (id: string, change: Partial<ChatMessage>) =>
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...change } : m)));

    // Track the final answer, citations, reasoning, and tool trace in closures so
    // the assistant turn can be persisted after the stream settles (state updates
    // are async/batched).
    let assembled = '';
    let grounded: Citation[] = [];
    let assembledThinking = '';
    const toolCalls: AskToolCall[] = [];
    const toolIndexById = new Map<string, number>();
    const startedAt = Date.now();

    try {
      await askCampaignStream(
        interviewId,
        question,
        token,
        {
          onActivity: (activity) => patch(assistantId, { activity }),
          onToken: (text) => {
            assembled += text;
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + text, activity: 'answering' } : m)),
            );
          },
          onCitations: (citations) => {
            grounded = citations;
            patch(assistantId, { citations });
          },
          onThinking: (text) => {
            assembledThinking += text;
            patch(assistantId, { thinking: assembledThinking });
          },
          onToolCall: ({ id, name, args }) => {
            toolIndexById.set(id, toolCalls.length);
            toolCalls.push({ id, name, args });
            patch(assistantId, { toolCalls: [...toolCalls] });
          },
          onToolResult: ({ id, name, ok, preview, csvExport, chart }) => {
            const idx = toolIndexById.get(id);
            const cards = { ...(csvExport ? { csvExport } : {}), ...(chart ? { chart } : {}) };
            if (idx !== undefined) {
              toolCalls[idx] = { ...toolCalls[idx], ok, preview, ...cards };
            } else {
              toolCalls.push({ id, name, ok, preview, ...cards });
            }
            patch(assistantId, { toolCalls: [...toolCalls] });
          },
          onTitle: (title) => {
            // A new thread gets an AI-generated title on its first turn; apply it
            // to the visible list and persist it for the team.
            if (!threadId) return;
            const tid = threadId;
            setThreads((prev) => prev.map((t) => (t.id === tid ? { ...t, title } : t)));
            setTitlePendingId((cur) => (cur === tid ? null : cur));
            void updateAskThreadTitle(supabase, tid, title).catch((err) =>
              captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.renameThread', {
                sessionId: interviewId,
              }),
            );
          },
        },
        {
          organizationId: activeOrg?.id,
          ...(interviewDataIds ? { interviewDataIds } : {}),
          ...(history.length ? { history } : {}),
        },
      );
      // Persist the completed assistant turn (answer + reasoning + tool trace).
      if (threadId) {
        void appendAskMessage(supabase, threadId, {
          id: assistantId,
          role: 'assistant',
          content: assembled,
          citations: grounded,
          ...(assembledThinking ? { thinking: assembledThinking } : {}),
          ...(toolCalls.length ? { toolCalls } : {}),
        }).catch((err) =>
          captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.persistAnswer', {
            sessionId: interviewId,
          }),
        );
      }
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'CampaignAskBox.askCampaignStream', {
        sessionId: interviewId,
      });
      patch(assistantId, { error: err instanceof Error ? err.message : 'Ask failed' });
    } finally {
      // Stamp the completion time as this turn's createdAt so a dropped or
      // reordered realtime echo can no longer leave the finished answer without a
      // sort key (which would sink it below a later question on the next merge).
      // Keep an echo that already landed: its DB persist time is authoritative, so
      // only fill when still absent.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                streaming: false,
                durationMs: Date.now() - startedAt,
                createdAt: m.createdAt ?? new Date().toISOString(),
              }
            : m,
        ),
      );
      setBusy(false);
      // Safety net: if the stream ended without a title event (e.g. it errored
      // before the title step), stop showing the skeleton for this thread.
      setTitlePendingId((cur) => (cur === threadId ? null : cur));
    }
  };

  return (
    <>
      <CampaignAskLauncher placeholder={placeholder} onOpen={handleOpen} variant={variant} />
      <CampaignAskModal
        campaignId={interviewId}
        open={open}
        onClose={handleClose}
        messages={messages}
        input={input}
        onInputChange={setInput}
        onSubmit={() => void handleSubmit()}
        busy={busy}
        onCitationClick={onCitationClick}
        resolveAuthorName={resolveAuthorName}
        placeholder={placeholder}
        threads={threads}
        activeThreadId={activeThreadId}
        titlePendingThreadId={titlePendingId}
        onNewConversation={handleNewConversation}
        onSwitchConversation={handleSwitchConversation}
        onDeleteConversation={handleDeleteConversation}
      />
    </>
  );
};
